import React, { useState } from "react";
import * as IO from "fp-ts/IO";

import DomainExpertiseIcon from "src/assets/images/domain-expertise.png";
import DomainLearningIcon from "src/assets/images/domain-learning.png";
import DomainPurposeIcon from "src/assets/images/domain-purpose.png";
import DomainRelationshipsIcon from "src/assets/images/domain-relationships.png";
import DomainWellBeingIcon from "src/assets/images/domain-wellbeing.png";
import img from "src/assets/images/fsci_report_logo.png";
import { Button } from "src/components/button/button";
import { Modal } from "src/components/modal/modal";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { isConstructHidden } from "src/config/hiddenConstructs";
import { ReportEdge, School } from "src/generated/types";
import { ConstructNotes, constructTitle } from "src/pages/reports/construct_notes";
import { isNotNil } from "src/utils/type_guards";

import { ExpertiseResources } from "./sections/expertise_resources/expertise_resources";
import { meta as expertiseResourcesMeta } from "./sections/expertise_resources/meta";
import { Purpose } from "./sections/purpose/purpose";
import { meta as purposeMeta } from "./sections/purpose/meta";
import { Relationships } from "./sections/relationships/relationships";
import { meta as relationshipsMeta } from "./sections/relationships/meta";
import { TeachingAndLearning } from "./sections/teaching_and_learning/teaching_and_learning";
import { WellBeing } from "./sections/well_being/well_being";
import { meta as wellBeingMeta } from "./sections/well_being/meta";

/*
 * Types
 */

interface ReportFullPageProps {
    readonly reports: Array<ReportEdge>;
    readonly school: School;
}

/*
 * Main
 */

export const ReportFullPage = (props: ReportFullPageProps): React.ReactElement => {
    const { reports, school } = props;

    const [isModalOpen, setModalOpen] = useState<boolean>(true);

    const convertToPdf: IO.IO<void> = () => window.print();

    return (
        <div className="main-page-wrap report full-report fsci-report">
            <div className="center">
                <div className="report-page-top">
                    <div className="report-page-title-line">
                        <img
                            alt="ACSI Flourishing School Culture Instrument"
                            className="fsci-report-printable-image"
                            src={img}
                        />
                        <PageTitle>{school.name}</PageTitle>
                    </div>
                    <Separator />
                </div>

                <div className="prepended-page-wrap">
                    <div className="report-additional-page page-0">
                        <div className="center">
                            <img alt="ACSI FSCI" className="fsci-report-printable-image" src={img} />
                            <PageTitle className="fsci-report-title">
                                Flourishing School Culture Initiative
                                <br />
                                {school && <span>{`For ${school.name}`}</span>}
                            </PageTitle>
                        </div>
                    </div>
                    <div className="report-additional-page page-1">
                        <div className="center">
                            <SectionTitle>Introduction</SectionTitle>
                            <Body>
                                Thank you for your school’s participation in the Flourishing School Culture Instrument
                                (FSCI). ACSI is pleased to partner with committed schools like yours across the country
                                to collect and leverage empirical data in the pursuit of insight around how and why
                                Christian schools flourish.
                            </Body>
                            <Body>
                                The first administration of the Flourishing Schools Culture Instrument (FSCI) tested
                                1,445 discrete variables across seven different survey groups: students; parents;
                                alumni; teachers; leaders/administrators; support staff; and board members. Between Fall
                                2018 and Spring 2019, over 15,000 survey responses were collected from Christian schools
                                of diverse size and geographic location, thereby making the FSCI the largest study of
                                flourishing in Christian schools to date. In subsequent administrations since then, with
                                Christian schools in both the United States and around the world, we continue to build
                                upon the insights around flourishing in Christian schools to deliver validated and
                                relevant insights.
                            </Body>
                            <Body>
                                The validated constructs identified through FSCI data analysis were mapped onto the
                                first ever research-based model of Christian school flourishing—the Flourishing School
                                Culture Model (FSCM). [Detailed information regarding the methodology utilized to
                                generate the insights in this school-level report can be found in the FSCI national
                                report, <em>Flourishing Schools: Research on Christian School Culture and Community</em>
                                .] The FSCM clusters the validated constructs for all seven survey groups into five
                                domains of flourishing: Purpose; Relationships; Teaching &amp; Learning; Expertise &amp;
                                Resources; and Well-Being. These domains provide a compelling and comprehensive picture
                                of the areas in which Christian schools can focus their efforts and resources in order
                                to promote a flourishing school culture and community.
                            </Body>
                            <Body>
                                All of your school’s data has been handled with the utmost care to ensure full
                                confidentiality. Your school’s profile, based on the results of the FSCI as mapped on to
                                the FSCM, is designed to highlight areas of existing strength as well as opportunities
                                for growth and improvement. Insights from the FSCI have the potential to inform
                                strategic planning and budgeting, professional development decisions, programmatic
                                priorities, marketing and promotional efforts, and much more.
                            </Body>
                            <Body>
                                As with most institutional survey research, it is important to note that FSCI findings
                                are best used when they are interpreted in light of school constituencies’ lived
                                experiences as collected through qualitative means. This means that schools will benefit
                                the most fully from this report when data is used to generate reflection, dialogue, and
                                collaborative planning for the present and the future. Please be sure to review the
                                report section entitled “Responding to Your School’s Results,” on the last two pages of
                                the report, for suggestions in this regard.
                            </Body>
                            <Body>
                                Thank you again for your school’s participation in the FSCI. We pray the insights in
                                this report will help your school to continue to grow in the five domains of Christian
                                school flourishing, and that ultimately—“May the Lord cause you to flourish, both you
                                and your children” (Psalm 115:14 NIV).
                            </Body>
                            <Body>
                                For questions regarding this report, please contact{" "}
                                <a href="mailto:assessmentsupport@acsi.org">assessmentsupport@acsi.org</a>.
                            </Body>
                        </div>
                    </div>
                    <div className="report-additional-page page-2">
                        <div className="center">
                            <SectionTitle>Overview of the Flourishing School Culture Model</SectionTitle>
                            <Body>
                                Analysis of the FSCI identified 35 validated constructs related to flourishing across
                                the seven groups of participants. These constructs have been grouped into a model with
                                five major domains of flourishing in Christian schools:{" "}
                                <em>Purpose; Relationships; Teaching &amp; Learning; Expertise &amp; Resources;</em> and{" "}
                                <em>Well-Being.</em> In the following pages, a definitional overview will be provided
                                for these domains, followed by a listing of specific constructs that make up each
                                domain.
                            </Body>
                            <div className="model-image-wrap report-page">
                                <img
                                    alt="Flourishing School Culture Model: Purpose, Relationships, Teaching and Learning, Expertise and Resources, and Well-Being support flourishing schools, educators, and students."
                                    src={img}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-3">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="blue">
                                            Defining <em>Purpose</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        A Christian perspective on education provides the <em>telos</em>, or purposeful
                                        aim, of Christian schooling. The FSCI identified a commitment on the part of all
                                        school constituencies to the central purposes of education as strongly connected
                                        with flourishing outcomes.
                                    </Body>
                                    <Body>
                                        Many of the distinctives of Christian education—meaning what is unique to
                                        Christian schools’ missions, values, and expected outcomes, as opposed to other
                                        sectors of education—fall within this domain. FSCI data analysis identified
                                        constructs related to the domain of Purpose for all seven survey groups
                                        (students; parents; alumni; teachers; leaders/administrators; support staff; and
                                        board members), below.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="purpose" src={DomainPurposeIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle>PURPOSE CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Responsibility</strong> – Leaders, teachers, and support staff feel a sense
                                    of shared ownership for school mission, success, and improvement.
                                </Body>
                                <Body>
                                    <strong>Holistic Teaching</strong> – Teaching involves helping students develop
                                    spiritually and emotionally (teaching the heart and soul, as well as the mind).
                                </Body>
                                <Body>
                                    <strong>Integrated Worldview</strong> – Christian worldview changes how we educate;
                                    there is no such thing as a secular sphere.
                                </Body>
                                <Body>
                                    <strong>God’s Story</strong> – Students believe they are a part of God’s bigger plan
                                    and can be used by him to “make a difference.”
                                </Body>
                                <Body>
                                    <strong>Questioning</strong> – Students have doubts about their faith, lack time to
                                    pray or study the Bible, and feel that most Christians are too judgmental.
                                </Body>
                                <Body>
                                    <strong>Partnership</strong> – Families feel they are a part of the school’s
                                    mission, and that their child’s spiritual development requires their partnering with
                                    and being involved at the school.
                                </Body>
                                <Body>
                                    <strong>Spiritual Formation</strong> – Alumni report that their Christian faith is
                                    stronger thanks to attending a Christian school, and they believe people can change
                                    with God’s help.
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-4">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="blue">
                                            Defining <em>Relationships</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        Education is a relational enterprise by nature. For Christian schools in
                                        particular, relationships are important because of the incarnational nature of
                                        Christian faith, expressed through community and in discipleship (John 1:14, 1
                                        Corinthians 12:12-27, Ephesians 4:16).
                                    </Body>
                                    <Body>
                                        The FSCI identified trust-filled, supportive, and authentic relationships
                                        between all school constituencies, as well as with the surrounding community, as
                                        key to flourishing outcomes. The constructs related to Relationship were
                                        identified for all seven survey groups (students; parents; alumni; teachers;
                                        leaders/administrators; support staff; and board members), below.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="purpose" src={DomainRelationshipsIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle>RELATIONSHIP CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Supportive Leadership</strong> – Principals are trusted, teachers feel that
                                    leaders “have our backs,” and leaders empower teachers and staff to make decisions.
                                </Body>
                                <Body>
                                    <strong>Leadership Interdependence</strong> – Board members have diverse
                                    backgrounds, and are transparent about and rely on others to offset their
                                    weaknesses.
                                </Body>
                                <Body>
                                    <strong>Family Relationships</strong> – Teachers “get to know” families, and
                                    frequent and systemic communication facilitates positive relationships.
                                </Body>
                                <Body>
                                    <strong>Community Engagement</strong> – The school engages with the surrounding
                                    community and local churches, and regularly taps into community resources including
                                    networking and resource-sharing with other schools.
                                </Body>
                                <Body>
                                    <strong>Mentoring Students</strong> – Staff point out talent in each student, help
                                    students see how they fit in God’s bigger plan, and are aware of students’ struggles
                                    at school or home.
                                </Body>
                                <Body>
                                    <strong>Insular Culture</strong> – The school shields students from the world’s
                                    brokenness, the school is independent from the surrounding community, and/or the
                                    student body lacks diversity.
                                </Body>
                                <Body>
                                    <strong>Christlike Teachers</strong> – Teachers show Christlike love, kindness, and
                                    care to students. Families feel students are cared about individually, including
                                    their spiritual development.
                                </Body>
                                <Body>
                                    <strong>Prosocial Orientation</strong> – Students not only enjoy helping others, but
                                    also are known by others (e.g., peers) for showing love and care.
                                </Body>
                                <Body>
                                    <strong>Caring Environment</strong> – From the perspective of school graduates,
                                    teachers were kind, students felt included in class, and students were protected
                                    from bullying.
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-5">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="blue">
                                            Defining <em>Teaching &amp; Learning</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        Schools are of course known as sites of learning for students. But if school
                                        cultures are to flourish, schools and educators themselves must also engage in
                                        learning. In fact, a school culture in which educators are committed to ongoing
                                        learning and improvement is linked to flourishing not only for the school and
                                        educators, but also for students.
                                    </Body>
                                    <Body>
                                        FSCI data analysis identified constructs related to{" "}
                                        <em>Teaching &amp; Learning</em> for five of the seven survey groups (alumni;
                                        teachers; leaders/administrators; support staff; and board members), as follows.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="purpose" src={DomainLearningIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle>TEACHING &amp; LEARNING CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Feedback</strong> – Feedback on teaching practice and classroom management
                                    is given regularly to facilitate adjustments in real-time.
                                </Body>
                                <Body>
                                    <strong>Collaboration</strong> – Learning from and with other teachers drives and
                                    inspires better teaching.
                                </Body>
                                <Body>
                                    <strong>Systems Thinking</strong> – When planning for change, the potential impact
                                    on the school, the classroom, students, and the overall system are considered.
                                </Body>
                                <Body>
                                    <strong>Data-Driven Improvement</strong> – Data is used to gauge school results and
                                    effectiveness, determine goal attainment, and address problems the school faces.
                                </Body>
                                <Body>
                                    <strong>Professional Development</strong> – PD is provided on-site and is subject-
                                    and role-specific.
                                </Body>
                                <Body>
                                    <strong>Outcomes Focus</strong> – Process doesn’t matter if it isn’t producing
                                    results, and change is distracting if it doesn’t lead to increases in student
                                    achievement.
                                </Body>
                                <Body>
                                    <strong>Culture of Improvement</strong> – Guided by school leadership and focused on
                                    the future, the school is continually improving/makes necessary changes to improve.
                                </Body>
                                <Body>
                                    <strong>Individualized Instruction</strong> – Students are helped to figure out how
                                    they learn best and to identify their natural strengths.
                                </Body>
                                <Body>
                                    <strong>Best Practice Orientation</strong> – Keeping up with best practices is
                                    prioritized and resources for doing so can be identified.
                                </Body>
                                <Body>
                                    <strong>Engaged Learning</strong> – Students engage in activities that nurture
                                    critical thinking, evaluating information, and problem solving.
                                </Body>
                                <Body>
                                    <strong>Behaviors for Learning</strong> – The classroom is orderly and well-managed,
                                    and teachers are organized and consistent in supporting student behaviors that
                                    contribute to learning.
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-6">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="blue">
                                            Defining <em>Expertise &amp; Resources</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        While a sense of purpose is essential for Christian school leaders and teachers,
                                        their ability to fulfill that purpose is necessarily dependent upon their
                                        expertise as educators. FCSI research shows that flourishing is connected to
                                        excellence in educational and school management practices.
                                    </Body>
                                    <Body>
                                        Constructs related to expertise &amp; resources were significant for five of the
                                        seven participant groups surveyed by the FSCI (parents; teachers;
                                        leaders/administrators; support staff; and board members).
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="purpose" src={DomainExpertiseIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle>EXPERTISE &amp; RESOURCES CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Qualified Staff</strong> – New teacher hires are credentialed
                                    (licensed/certified) and have classroom experience.
                                </Body>
                                <Body>
                                    <strong>Responsiveness to Special Needs</strong> – Teaching staff works together to
                                    serve students with special needs, aided by processes and resources for identifying
                                    and responding to those needs.
                                </Body>
                                <Body>
                                    <strong>Resources</strong> – Materials and resources for teaching, including
                                    technology, are sufficient, and the school building is in good physical condition.
                                </Body>
                                <Body>
                                    <strong>Resource Planning</strong> – A strategic financial plan and master
                                    facilities plan is in place, and financial planning is a strength of the board.
                                </Body>
                                <Body>
                                    <strong>Resource Constraints</strong> – The school has financial resources to
                                    operate effectively; or, we could be more effective as a school if not for fiscal
                                    constraints, and we lack the resources we need to make changes in our school.
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-7">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="blue">
                                            Defining <em>Well-Being</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        Well-being is an important domain for both student and educator flourishing. The
                                        FSCI identified three constructs related to well-being—one for teachers and
                                        leaders (Stress), and two for students (Healthy Living and Resilience). This
                                        domain and related constructs demonstrate that the well-being of educators and
                                        students is not a secondary concern—but an essential contributor to flourishing
                                        outcomes.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="purpose" src={DomainWellBeingIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle>WELL-BEING CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Stress</strong> – Constant feelings of stress and being overwhelmed
                                    accompany a lack of time to prepare for instruction (Teachers) or to focus on
                                    physical health (Leaders).
                                </Body>
                                <Body>
                                    <strong>Healthy Living</strong> – Students are happy with their physical health,
                                    including sufficient exercise and a healthy diet.
                                </Body>
                                <Body>
                                    <strong>Resilience</strong> – Students handle stress effectively and respond well
                                    to/bounce back from difficult situations.
                                </Body>
                            </div>
                        </div>
                    </div>
                </div>

                <Purpose reports={reports} />
                <Relationships reports={reports} />
                <TeachingAndLearning reports={reports} />
                <ExpertiseResources reports={reports} />
                <WellBeing reports={reports} />

                <div className="prepended-page-wrap">
                    <div className="report-additional-page page-8">
                        <div className="center">
                            <>
                                <SectionTitle className="section-title-extra-spacing">Results Summary</SectionTitle>
                                {reports &&
                                    reports.map((report, i) => {
                                        return (
                                            <Body className="more-top" key={i}>
                                                <h1>{report.node?.year}</h1>
                                                <strong>Purpose</strong>
                                                <ul>
                                                    <li>
                                                        PU01. Responsibility{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.purpose.responsibility.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        PU02. Holistic Teaching{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.purpose.holisticTeaching.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        PU03. Integrated Worldview{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.purpose.integratedWorldView.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        PU04. God’s Story{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.purpose.godsStory.toFixed(2),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    {!isConstructHidden("questioning") &&
                                                        isNotNil(report.node?.results.raw.purpose.questioning) && (
                                                            <li>
                                                                PU05. {constructTitle(purposeMeta.questioning)}{" "}
                                                                {Math.round(
                                                                    Number(
                                                                        report.node?.results.raw.purpose.questioning.toFixed(
                                                                            2,
                                                                        ),
                                                                    ) * 100,
                                                                )}
                                                            </li>
                                                        )}
                                                    <li>
                                                        PU06. Partnership{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.purpose.partnership.toFixed(2),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        PU07. Spiritual Formation{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.purpose.spiritualFormation.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                </ul>
                                                <strong>Relationships</strong>
                                                <ul>
                                                    <li>
                                                        RE01. Supportive Leadership{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.relationships.supportiveLeadership.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        RE02. Leadership Interdependence{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.relationships.leadershipInterdependence.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        RE03. Family Relationships{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.relationships.familyRelationships.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        RE04. Community Engagement{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.relationships.communityEngagement.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        RE05. Mentoring Students{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.relationships.mentoringStudents.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        RE06. {constructTitle(relationshipsMeta.insularCulture)}{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.relationships.insularCulture.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        RE07. Christlike Teachers{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.relationships.christlikeTeachers.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        RE08. Prosocial Orientation{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.relationships.prosocialOrientation.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        RE09. Caring Environment{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.relationships.caringEnvironment.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                </ul>
                                                <strong>Teaching & Learning</strong>
                                                <ul>
                                                    <li>
                                                        TL01. Feedback{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.teachingAndLearning.feedback.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        TL02. Collaboration{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.teachingAndLearning.collaboration.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        TL03. Systems Thinking{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.teachingAndLearning.systemsThinking.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        TL04. Data-Driven Improvement{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.teachingAndLearning.dataDrivenImprovement.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        TL05. Professional Development{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.teachingAndLearning.professionalDevelopment.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        TL06. Outcomes Focus{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.teachingAndLearning.outcomesFocus.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        TL07. Culture of Improvement{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.teachingAndLearning.cultureOfImprovement.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        TL08. Individualized Instruction{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.teachingAndLearning.individualizedInstruction.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        TL09. Best Practice Orientation{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.teachingAndLearning.bestPracticeOrientation.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        TL10. Engaged Learning{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.teachingAndLearning.engagedLearning.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        TL11. Behaivors for Learning{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.teachingAndLearning.behaviorsForLearning.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                </ul>
                                                <strong>Expertise & Resources</strong>
                                                <ul>
                                                    <li>
                                                        ER01. Qualified Staff{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.expertiseAndResources.qualifiedStaff.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        ER02. Responsiveness to Special Needs{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.expertiseAndResources.responsivenessToSpecialNeeds.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        ER03. Resources{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.expertiseAndResources.resources.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        ER04. Resource Planning{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.expertiseAndResources.resourcePlanning.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        ER05.{" "}
                                                        {constructTitle(expertiseResourcesMeta.resourceConstraint)}{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.expertiseAndResources.resourceConstraint.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                </ul>
                                                <strong>Well-Being</strong>
                                                <ul>
                                                    <li>
                                                        WB01. {constructTitle(wellBeingMeta.stress)}{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.wellBeing.stress.toFixed(2),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        WB02. Healthy Living{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.wellBeing.healthyLiving.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                    <li>
                                                        WB03. Resilience{" "}
                                                        {Math.round(
                                                            Number(
                                                                report.node?.results.raw.wellBeing.resilience.toFixed(
                                                                    2,
                                                                ),
                                                            ) * 100,
                                                        )}
                                                    </li>
                                                </ul>
                                            </Body>
                                        );
                                    })}
                                <ConstructNotes
                                    metas={[purposeMeta, relationshipsMeta, expertiseResourcesMeta, wellBeingMeta]}
                                />
                            </>
                        </div>
                    </div>
                </div>

                <div className="prepended-page-wrap">
                    <div className="report-additional-page page-8">
                        <div className="center">
                            <>
                                <SectionTitle className="section-title-extra-spacing">
                                    Submission Size Summary
                                </SectionTitle>
                                {reports &&
                                    reports.map((report, i) => {
                                        return (
                                            <Body className="more-top" key={i}>
                                                <h1>{report.node?.year}</h1>
                                                <strong>Students</strong>
                                                <ul>
                                                    <li>
                                                        Constituent group size:{" "}
                                                        {report.node?.constituentGroups.students}
                                                    </li>
                                                    <li>
                                                        Total number of responses:{" "}
                                                        {report.node?.surveysCompleted.students}
                                                    </li>
                                                    <li>
                                                        Total completion percentage:{" "}
                                                        {(
                                                            (report.node?.surveysCompleted.students /
                                                                report.node?.constituentGroups.students) *
                                                            100.0
                                                        ).toFixed(1)}
                                                        %
                                                    </li>
                                                </ul>
                                                <strong>Teachers</strong>
                                                <ul>
                                                    <li>
                                                        Constituent group size:{" "}
                                                        {report.node?.constituentGroups.teachers}
                                                    </li>
                                                    <li>
                                                        Total number of responses:{" "}
                                                        {report.node?.surveysCompleted.teachers}
                                                    </li>
                                                    <li>
                                                        Total completion percentage:{" "}
                                                        {(
                                                            (report.node?.surveysCompleted.teachers /
                                                                report.node?.constituentGroups.teachers) *
                                                            100.0
                                                        ).toFixed(1)}
                                                        %
                                                    </li>
                                                </ul>
                                                <strong>Administrators</strong>
                                                <ul>
                                                    <li>
                                                        Constituent group size:{" "}
                                                        {report.node?.constituentGroups.administrators}
                                                    </li>
                                                    <li>
                                                        Total number of responses:{" "}
                                                        {report.node?.surveysCompleted.administrators}
                                                    </li>
                                                    <li>
                                                        Total completion percentage:{" "}
                                                        {(
                                                            (report.node?.surveysCompleted.administrators /
                                                                report.node?.constituentGroups.administrators) *
                                                            100.0
                                                        ).toFixed(1)}
                                                        %
                                                    </li>
                                                </ul>
                                                <strong>Support Staff</strong>
                                                <ul>
                                                    <li>
                                                        Constituent group size:{" "}
                                                        {report.node?.constituentGroups.supportStaff}
                                                    </li>
                                                    <li>
                                                        Total number of responses:{" "}
                                                        {report.node?.surveysCompleted.supportStaff}
                                                    </li>
                                                    <li>
                                                        Total completion percentage:{" "}
                                                        {(
                                                            (report.node?.surveysCompleted.supportStaff /
                                                                report.node?.constituentGroups.supportStaff) *
                                                            100.0
                                                        ).toFixed(1)}
                                                        %
                                                    </li>
                                                </ul>
                                                <strong>Parents</strong>
                                                <ul>
                                                    <li>
                                                        Constituent group size: {report.node?.constituentGroups.parents}
                                                    </li>
                                                    <li>
                                                        Total number of responses:{" "}
                                                        {report.node?.surveysCompleted.parents}
                                                    </li>
                                                    <li>
                                                        Total completion percentage:{" "}
                                                        {(
                                                            (report.node?.surveysCompleted.parents /
                                                                report.node?.constituentGroups.parents) *
                                                            100.0
                                                        ).toFixed(1)}
                                                        %
                                                    </li>
                                                </ul>
                                                <strong>Alumni</strong>
                                                <ul>
                                                    <li>
                                                        Constituent group size: {report.node?.constituentGroups.alumni}
                                                    </li>
                                                    <li>
                                                        Total number of responses:{" "}
                                                        {report.node?.surveysCompleted.alumni}
                                                    </li>
                                                    <li>
                                                        Total completion percentage:{" "}
                                                        {(
                                                            (report.node?.surveysCompleted.alumni /
                                                                report.node?.constituentGroups.alumni) *
                                                            100.0
                                                        ).toFixed(1)}
                                                        %
                                                    </li>
                                                </ul>
                                                <strong>Board Members</strong>
                                                <ul>
                                                    <li>
                                                        Constituent group size:{" "}
                                                        {report.node?.constituentGroups.boardMembers}
                                                    </li>
                                                    <li>
                                                        Total number of responses:{" "}
                                                        {report.node?.surveysCompleted.boardMembers}
                                                    </li>
                                                    <li>
                                                        Total completion percentage:{" "}
                                                        {(
                                                            (report.node?.surveysCompleted.boardMembers /
                                                                report.node?.constituentGroups.boardMembers) *
                                                            100.0
                                                        ).toFixed(1)}
                                                        %
                                                    </li>
                                                </ul>
                                            </Body>
                                        );
                                    })}
                            </>
                        </div>
                    </div>
                </div>
            </div>

            <Modal isOpen={isModalOpen} setOpen={setModalOpen} title="Print instructions">
                <Body>
                    You can either print a copy of your report or save it as a PDF. To save it as a PDF, click print,
                    expand the printer dropdown, and choose "Save as PDF."
                </Body>
                <Button
                    onClick={(): void => {
                        setModalOpen(false);
                        setTimeout(() => convertToPdf(), 1000);
                    }}
                >
                    Ok
                </Button>
            </Modal>
        </div>
    );
};
